import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Clapperboard } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';

const GoogleBtn = () => {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" onClick={start} variant="outline" data-testid="google-login-btn"
      className="w-full rounded-full h-11 border-white/15 bg-white/5 hover:bg-white/10">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
      Continue with Google
    </Button>
  );
};

export function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#090A0F]">
      <div className="hidden lg:block relative grain overflow-hidden">
        <img src="https://images.unsplash.com/photo-1627244714766-94dab62ed964?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#090A0F] via-[#090A0F]/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF4D00] to-[#8B5CF6]"><Clapperboard className="w-5 h-5 text-white" /></span>
            <span className="font-heading font-extrabold text-lg">TubeEditor <span className="text-gradient">AI</span></span>
          </Link>
          <div>
            <div className="font-heading text-3xl font-bold leading-tight max-w-sm">AI does the first 80% of the editing. You approve the result.</div>
            <div className="text-slate-400 mt-4 font-mono text-sm">Upload → Analyze → Edit → Revoice → Enhance → Publish</div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-2">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      nav('/app');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Log in">
      <p className="text-slate-400 text-sm mb-6">New here? <Link to="/signup" className="text-[#FF884D] hover:text-white">Create an account</Link></p>
      <GoogleBtn />
      <div className="flex items-center gap-3 my-5"><div className="h-px bg-white/10 flex-1" /><span className="text-xs text-slate-500 font-mono">OR</span><div className="h-px bg-white/10 flex-1" /></div>
      <form onSubmit={submit} className="space-y-4">
        <div><Label className="text-slate-300">Email</Label>
          <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 bg-[#12141D] border-white/12 h-11" placeholder="you@channel.com" /></div>
        <div><Label className="text-slate-300">Password</Label>
          <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 bg-[#12141D] border-white/12 h-11" placeholder="••••••••" /></div>
        <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full rounded-full h-11 font-semibold">
          {busy ? 'Logging in…' : 'Log in'}
        </Button>
      </form>
    </AuthShell>
  );
}
